import BigNumber from "bignumber.js";
import type {CartState} from "@/components/Providers/CartProvider.tsx";
import {formatter} from "@/Helpers/formatter.ts";

export type InPersonPaymentMethod = "cash" | "check";

export type InPersonPaymentOutcome = {
    error?: string;
    acknowledgement?: string;
};

export type InPersonPaymentResponse = {
    data?: {
        paidCents?: unknown;
        changeCents?: unknown;
        alreadyPaid?: unknown;
    };
};

export type InPersonPaymentResult = {
    message: string;
    alertVariant: CartState["alertVariant"];
    needsAcknowledgement: boolean;
};

const LABELS: Record<InPersonPaymentMethod, string> = {
    cash: "Cash",
    check: "Check",
};

const centsFrom = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) ? Math.round(value) : null;

const toCents = (dollars: number): number =>
    BigNumber(dollars).times(100).integerValue(BigNumber.ROUND_FLOOR).toNumber();

const money = (cents: number): string => formatter.format(BigNumber(cents).div(100).toNumber());

const cashShortfallMessage = (
    paidCents: number,
    requestedCents: number,
    changeCents: number | null,
    tenderedCents: number | undefined,
): string => {
    const shortfall = `Only ${money(paidCents)} of the ${money(requestedCents)} could be applied to this cart.`;
    const tendered = centsFrom(tenderedCents);

    if (changeCents === null) {
        return `${shortfall} Return ${money(requestedCents - paidCents)} to the customer.`;
    }

    const changeAlreadyShown = tendered === null ? 0 : tendered - requestedCents;

    if (changeAlreadyShown > 0) {
        return `${shortfall} Change due is ${money(changeCents)}, not the ${money(changeAlreadyShown)} shown before.`;
    }

    return `${shortfall} Return ${money(changeCents)} to the customer.`;
};

export const buildInPersonPaymentResult = (
    method: InPersonPaymentMethod,
    payNowAmount: number,
    body: InPersonPaymentResponse | null,
    tenderedCents?: number,
): InPersonPaymentResult => {
    if (body?.data?.alreadyPaid === true) {
        const collected = centsFrom(tenderedCents);

        return {
            message:
                method === "cash"
                    ? `This cart was already paid, so no payment was taken. Return the ${money(collected ?? toCents(payNowAmount))} you collected to the customer.`
                    : "This cart was already paid, so no payment was taken. Hand the check back to the customer.",
            alertVariant: "warning",
            needsAcknowledgement: true,
        };
    }

    const paidCents = centsFrom(body?.data?.paidCents);

    const requestedCents = toCents(payNowAmount);

    if (paidCents === null) {
        return {
            message: `${LABELS[method]} payment of ${money(requestedCents)} submitted.`,
            alertVariant: "success",
            needsAcknowledgement: false,
        };
    }

    if (paidCents >= requestedCents) {
        return {
            message: `${LABELS[method]} payment of ${money(paidCents)} submitted.`,
            alertVariant: "success",
            needsAcknowledgement: false,
        };
    }

    return {
        message:
            method === "cash"
                ? cashShortfallMessage(
                      paidCents,
                      requestedCents,
                      centsFrom(body?.data?.changeCents),
                      tenderedCents,
                  )
                : `Only ${money(paidCents)} of the ${money(requestedCents)} could be applied to this cart. The ${money(requestedCents - paidCents)} difference will need to be refunded.`,
        alertVariant: "warning",
        needsAcknowledgement: true,
    };
};
